// Iter 82.9 Sprint 2 / Phase E3.1 — Payroll Run Timeline.
//
// Read-only audit/compliance visualisation. Consumes the existing
// GET /api/payroll/run/{id}/audit endpoint — no new backend surface
// required (per operator instruction).
//
// Renders a vertical timeline of state transitions, overrides, and
// system events with colour-coded dots, actor + timestamp, and a
// click-to-expand payload drawer.
import { useEffect, useState } from "react";
import {
    PlusCircle, Calculator, PaperPlaneRight, CheckCircle, XCircle,
    Lock, HandCoins, ArrowsClockwise, Warning, Trash, GitBranch,
    Wrench, Snowflake,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { Badge } from "../ui/badge";

const ACTION_META = {
    create:               { icon: PlusCircle,     tone: "text-slate-700 bg-slate-100",     label: "Run created" },
    calculate:            { icon: Calculator,     tone: "text-blue-700 bg-blue-50",        label: "Compute engine ran" },
    freeze_attendance:    { icon: Snowflake,      tone: "text-cyan-700 bg-cyan-50",        label: "Attendance frozen" },
    set_manual_inputs:    { icon: Wrench,         tone: "text-amber-700 bg-amber-50",      label: "Manual inputs updated" },
    submit:               { icon: PaperPlaneRight,tone: "text-indigo-700 bg-indigo-50",    label: "Submitted for approval" },
    approve:              { icon: CheckCircle,    tone: "text-emerald-700 bg-emerald-50",  label: "Approved" },
    reject:               { icon: XCircle,        tone: "text-rose-700 bg-rose-50",        label: "Rejected" },
    revert_to_draft:      { icon: ArrowsClockwise,tone: "text-zinc-700 bg-zinc-100",       label: "Reverted to Draft" },
    lock:                 { icon: Lock,           tone: "text-violet-700 bg-violet-50",    label: "Run locked (immutable)" },
    disburse:             { icon: HandCoins,      tone: "text-emerald-700 bg-emerald-50",  label: "Disbursed to bank" },
    discard:              { icon: Trash,          tone: "text-rose-700 bg-rose-50",        label: "Discarded" },
    override_exception:   { icon: Warning,        tone: "text-amber-700 bg-amber-50",      label: "Exception overridden" },
    create_correction:    { icon: GitBranch,      tone: "text-indigo-700 bg-indigo-50",    label: "Correction run linked" },
};

function formatDateTime(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
    });
}

function eventTitle(ev) {
    const meta = ACTION_META[ev.action] || { label: ev.action };
    return meta.label;
}

export default function PayrollRunTimeline({ runId, onSelectPayload }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancel = false;
        (async () => {
            setLoading(true);
            try {
                const r = await api.get(`/payroll/run/${runId}/audit`);
                if (!cancel) setEvents(r.data.items || []);
            } catch (_e) {
                if (!cancel) setEvents([]);
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => { cancel = true; };
    }, [runId]);

    if (loading) {
        return (
            <div className="p-6 text-sm text-slate-500" data-testid="timeline-loading">
                Loading run audit timeline…
            </div>
        );
    }
    if (events.length === 0) {
        return (
            <div className="p-6 text-sm text-slate-500" data-testid="timeline-empty">
                No audit events yet — run hasn't transitioned since creation.
            </div>
        );
    }

    return (
        <ol className="relative border-s border-slate-200 ml-3 my-4 space-y-6" data-testid="payroll-timeline">
            {events.map((ev, idx) => {
                const meta = ACTION_META[ev.action] || {
                    icon: Wrench, tone: "text-slate-700 bg-slate-100", label: ev.action,
                };
                const Icon = meta.icon;
                return (
                    <li key={ev.id || idx} className="ms-6" data-testid={`timeline-event-${ev.action}`}>
                        <span className={`absolute -start-3.5 flex items-center justify-center w-7 h-7 rounded-full ring-2 ring-white ${meta.tone}`}>
                            <Icon size={14} weight="bold" />
                        </span>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="text-sm font-semibold text-slate-800">{eventTitle(ev)}</h4>
                                    {ev.from_state && ev.to_state && ev.from_state !== ev.to_state && (
                                        <Badge variant="outline" className="text-[10px] font-mono">
                                            {ev.from_state} → {ev.to_state}
                                        </Badge>
                                    )}
                                </div>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {ev.actor_name || ev.actor_email || "System"}
                                    {" · "}
                                    <time>{formatDateTime(ev.created_at)}</time>
                                </p>
                                {ev.payload && Object.keys(ev.payload).length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => onSelectPayload && onSelectPayload(ev)}
                                        className="mt-1 text-[11px] text-indigo-600 hover:text-indigo-800 underline-offset-2 hover:underline"
                                        data-testid={`timeline-payload-${ev.action}`}
                                    >
                                        View payload ({Object.keys(ev.payload).length} field{Object.keys(ev.payload).length === 1 ? "" : "s"})
                                    </button>
                                )}
                            </div>
                        </div>
                    </li>
                );
            })}
        </ol>
    );
}
